import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookings, identity } from '@repletoryreach/sdk-api';
import { Button, Divider, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { SegmentedControl, Select, Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { ColourPicker } from '../components/ColourPicker';
import { BOOKINGS_KEY, bookingErrorText, SWATCHES, useDateLocale, WEEKDAY_CODES } from '../lib/data';
import { formatInZone, todayIn, zonedIso } from '../lib/time';
import { hoursProblem, WeekHoursEditor } from './WeekHoursEditor';

const MUTED = 'var(--rr-color-text-muted)';

interface Draft {
  name: string;
  user_id: string;
  email: string;
  phone: string;
  colour: string | null;
  ownHours: boolean;
  hours: bookings.StaffHoursSlot[];
  active: boolean;
}

function toDraft(s: bookings.Staff | null, businessWeekly: bookings.StaffHoursSlot[]): Draft {
  return {
    name: s?.name ?? '',
    user_id: s?.user_id ?? '',
    email: s?.email ?? '',
    phone: s?.phone ?? '',
    colour: s?.colour ?? SWATCHES[1],
    ownHours: !!s?.hours,
    // Own hours start from the business's, which is what most people change a little.
    hours: s?.hours ?? businessWeekly,
    active: s?.active ?? true
  };
}

/** "datetime-local" value to an instant in the organisation's zone. */
function localInputToIso(v: string, tz: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(v);
  return m ? zonedIso(m[1]!, m[2]!, tz) : null;
}

export function StaffDrawer({ target, canEdit, tz, onClose, onSaved }: {
  target: bookings.Staff | 'new' | null;
  canEdit: boolean;
  tz: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const existing = target && target !== 'new' ? target : null;
  const open = target !== null;
  const hoursQ = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours(), enabled: open, staleTime: 5 * 60_000 });
  const usersQ = useQuery({ queryKey: ['users', 'for-bookings'], queryFn: () => identity.listUsers({ limit: 200 }), enabled: open && canEdit, retry: false });
  const businessWeekly = (hoursQ.data?.data.weekly ?? []) as bookings.StaffHoursSlot[];

  const [d, setD] = useState<Draft>(() => toDraft(null, []));
  const [initial, setInitial] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const draft = toDraft(existing, businessWeekly);
    setD(draft);
    setInitial(JSON.stringify(draft));
    setTried(false);
    setError(null);
  }, [open, existing?.id, hoursQ.isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const dirty = open && JSON.stringify(d) !== initial;
  const hoursInvalid = d.ownHours && WEEKDAY_CODES.some((day) => hoursProblem(d.hours.filter((s) => s.day === day)) !== null);
  const emailInvalid = d.email.trim() !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim());
  const nameMissing = !d.name.trim();

  function requestClose() {
    if (!dirty || !canEdit) { onClose(); return; }
    openConfirmModal({
      title: t('bookings.discard.title'),
      children: <Text role="bodySmall">{t('bookings.discard.body')}</Text>,
      labels: { confirm: t('action.discard'), cancel: t('action.keep') },
      confirmProps: { color: 'red' },
      onConfirm: onClose
    });
  }

  async function save() {
    setTried(true);
    if (nameMissing || hoursInvalid || emailInvalid) return;
    setBusy(true);
    setError(null);
    try {
      const body: bookings.StaffInput = {
        name: d.name.trim(),
        user_id: d.user_id || null,
        email: d.email.trim() || null,
        phone: d.phone.trim() || null,
        colour: d.colour,
        hours: d.ownHours ? d.hours : null,
        active: d.active
      };
      const saved = existing ? (await bookings.updateStaff(existing.id, body)).data : (await bookings.createStaff(body)).data;
      notifySuccess(t(existing ? 'bookings.staff.toast.saved' : 'bookings.staff.toast.created', { name: saved.name }), t('bookings.staff.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(bookingErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  const ro = !canEdit;
  const users = usersQ.data?.data ?? [];

  return (
    <Drawer opened={open} onClose={requestClose} title={existing ? existing.name : t('bookings.staff.new')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md" data-testid="staff-editor">
          {ro && <Alert color="info">{t('bookings.readOnly')}</Alert>}
          <TextInput label={t('common.name')} value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} required maxLength={120} disabled={ro}
            description={t('bookings.staff.nameHint')}
            error={tried && nameMissing ? t('bookings.staff.error.name') : undefined} />

          {canEdit && (
            <Select
              label={t('bookings.staff.teamMember')}
              value={d.user_id}
              onChange={(e) => {
                const id = e.currentTarget.value;
                const u = users.find((x) => x.id === id);
                set({
                  user_id: id,
                  ...(u && !d.name.trim() ? { name: u.display_name ?? '' } : {}),
                  ...(u && !d.email.trim() ? { email: u.email ?? '' } : {})
                });
              }}
              data={[{ value: '', label: t('bookings.staff.noTeamMember') }, ...users.map((u) => ({ value: u.id, label: u.display_name || u.email || t('common.unknown') }))]}
              description={t('bookings.staff.teamMemberHint')}
            />
          )}

          <Group gap="sm" grow align="flex-start" wrap="wrap">
            <TextInput type="email" label={t('common.email')} value={d.email} onChange={(e) => set({ email: e.currentTarget.value })} disabled={ro}
              error={tried && emailInvalid ? t('bookings.staff.error.email') : undefined} />
            <TextInput type="tel" label={t('bookings.field.phone')} value={d.phone} onChange={(e) => set({ phone: e.currentTarget.value })} disabled={ro} />
          </Group>

          <ColourPicker label={t('bookings.staff.colour')} value={d.colour} onChange={(c) => set({ colour: c })} disabled={ro} />

          <Divider />

          <Stack gap="xs">
            <Text role="inputLabel">{t('bookings.staff.hours')}</Text>
            <SegmentedControl
              value={d.ownHours ? 'own' : 'business'}
              onChange={(v) => set({ ownHours: v === 'own', hours: d.hours.length ? d.hours : businessWeekly })}
              disabled={ro}
              data={[
                { value: 'business', label: t('bookings.staff.hours.business') },
                { value: 'own', label: t('bookings.staff.hours.own') }
              ]}
            />
            {!d.ownHours && (
              <Text role="caption" c={MUTED}>
                {t('bookings.staff.hours.businessHint')}{' '}
                <Link to="/settings/organisation">{t('bookings.staff.hours.businessLink')}</Link>
              </Text>
            )}
          </Stack>
          {d.ownHours && <WeekHoursEditor value={d.hours} onChange={(hours) => set({ hours })} disabled={ro} />}

          <Switch label={t('bookings.staff.active')} description={t('bookings.staff.activeHint')} checked={d.active} onChange={(on) => set({ active: on })} disabled={ro} />

          {error && <Alert color="error" role="alert">{error}</Alert>}

          <Group gap="sm" justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button variant="default" onClick={requestClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
            {canEdit && <Button type="submit" loading={busy}>{existing ? t('action.saveChanges') : t('bookings.staff.create')}</Button>}
          </Group>

        </Stack>
      </form>
      {existing && (
        <Stack gap="md" style={{ marginTop: 16 }}>
          <Divider />
          <TimeOffSection staff={existing} canEdit={canEdit} tz={tz} />
        </Stack>
      )}
    </Drawer>
  );
}

/** Leave, training, a dentist's appointment: times this person cannot be booked. */
function TimeOffSection({ staff, canEdit, tz }: { staff: bookings.Staff; canEdit: boolean; tz: string }) {
  const t = useT();
  const qc = useQueryClient();
  const locale = useDateLocale();
  const today = todayIn(tz);
  const [from, setFrom] = useState(`${today}T08:00`);
  const [to, setTo] = useState(`${today}T17:00`);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const q = useQuery({
    queryKey: [BOOKINGS_KEY, 'time-off', staff.id],
    queryFn: () => bookings.listTimeOff(staff.id, { from: zonedIso(today, '00:00', tz) })
  });
  const rows = [...(q.data?.data ?? [])].sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  const add = useMutation({
    mutationFn: (body: bookings.TimeOffInput) => bookings.addTimeOff(staff.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
      setReason('');
      notifySuccess(t('bookings.timeOff.toast.added', { name: staff.name }), t('bookings.staff.title'));
    },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.staff.title'))
  });
  const remove = useMutation({
    mutationFn: (id: string) => bookings.removeTimeOff(staff.id, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] }),
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.staff.title'))
  });

  function submit() {
    const a = localInputToIso(from, tz);
    const b = localInputToIso(to, tz);
    if (!a || !b) { setProblem(t('bookings.timeOff.error.times')); return; }
    if (new Date(a).getTime() >= new Date(b).getTime()) { setProblem(t('bookings.timeOff.error.order')); return; }
    setProblem(null);
    add.mutate({ starts_at: a, ends_at: b, reason: reason.trim() || undefined });
  }

  const fmt = (iso: string) => formatInZone(iso, tz, locale, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

  return (
    <Stack gap="sm" data-testid="time-off">
      <Stack gap={2}>
        <Text role="inputLabel">{t('bookings.timeOff.title')}</Text>
        <Text role="caption" c={MUTED}>{t('bookings.timeOff.hint')}</Text>
      </Stack>
      {q.error && <Alert color="error" role="alert">{bookingErrorText(t, q.error)}</Alert>}
      {!q.isPending && rows.length === 0 && <Text role="bodySmall" c={MUTED}>{t('bookings.timeOff.empty')}</Text>}
      {rows.map((r) => (
        <Group key={r.id} gap="sm" justify="space-between" wrap="nowrap" className="rr-bookings__row">
          <Stack gap={0}>
            <Text role="bodySmall" fw={600}>{t('bookings.timeOff.range', { from: fmt(r.starts_at), to: fmt(r.ends_at) })}</Text>
            {r.reason && <Text role="caption" c={MUTED}>{r.reason}</Text>}
          </Stack>
          {canEdit && (
            <Button size="xs" variant="subtle" color="red" loading={remove.isPending && remove.variables === r.id}
              aria-label={t('bookings.timeOff.removeFor', { from: fmt(r.starts_at) })}
              onClick={() => remove.mutate(r.id)}>
              {t('action.remove')}
            </Button>
          )}
        </Group>
      ))}
      {canEdit && (
        <Stack gap="xs">
          <Group gap="xs" wrap="wrap" align="flex-start">
            <TextInput type="datetime-local" label={t('bookings.timeOff.from')} value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            <TextInput type="datetime-local" label={t('bookings.timeOff.to')} value={to} onChange={(e) => setTo(e.currentTarget.value)} />
          </Group>
          <TextInput label={t('bookings.timeOff.reason')} value={reason} onChange={(e) => setReason(e.currentTarget.value)} maxLength={200}
            description={t('bookings.timeOff.reasonHint')} error={problem ?? undefined} />
          <div><Button size="xs" variant="default" loading={add.isPending} onClick={submit}>{t('bookings.timeOff.add')}</Button></div>
        </Stack>
      )}
    </Stack>
  );
}
